import { useTranslation } from "react-i18next";
import { Group, Slider, Stack, Text, UnstyledButton } from "@mantine/core";
import { spacing } from "@xplatform/design-system";
import { Q1_SCALE, type Q1Score } from "@models/q1";
import "./Q1Panel.scss";

export interface Q1Values {
  /** Usability 1-5, required on both decisions (D2). `null` until chosen. */
  score: Q1Score | null;
  /** Model distinctness 0-100; present only when the image carried one. */
  distinctness: number | null;
  /** Model accuracy 0-100; present only when the image carried one. */
  accuracy: number | null;
}

interface Q1PanelProps {
  value: Q1Values;
  onChange: (next: Q1Values) => void;
  /**
   * Whether the image carries model distinctness/accuracy values. The two
   * sliders render ONLY when true (I-11); in our pipeline they are absent, so
   * the panel shows the usability scale alone and the values are never sent.
   */
  hasModelValues: boolean;
}

/**
 * Q1 — how usable is the redraw compared to the source? A required 1-5
 * usability scale, plus two optional 0-100 sliders (distinctness, technically
 * correct) that render ONLY when the image carries a model value. Wording is
 * reused from the reviewer-app (`Q1_SCALE`); it is localised via i18n keyed by
 * the scale value.
 */
export function Q1Panel({ value, onChange, hasModelValues }: Q1PanelProps) {
  const { t } = useTranslation();

  return (
    <Stack gap={spacing.md} className="q1-panel">
      <div>
        <Text fw={600} size="sm" mb={spacing.xs}>
          {t("review.q1.heading")}
        </Text>
        {/* One row of five score buttons (owner, 2026-11-04); the label of the chosen score is shown below. */}
        <Group gap={spacing.xs} role="radiogroup" aria-label={t("review.q1.heading")} wrap="nowrap">
          {Q1_SCALE.map((opt) => {
            const active = value.score === opt.value;
            return (
              <UnstyledButton
                key={opt.value}
                role="radio"
                aria-checked={active}
                title={`${t(`review.q1.scale.${opt.value}.label`)} — ${t(`review.q1.scale.${opt.value}.description`)}`}
                className={`q1-panel__score${active ? " q1-panel__score--active" : ""}`}
                onClick={() => onChange({ ...value, score: opt.value as Q1Score })}
              >
                <Text span fw={600}>{opt.value}</Text>
                <Text span size="xs" c={active ? undefined : "dimmed"}>
                  {t(`review.q1.scale.${opt.value}.label`)}
                </Text>
              </UnstyledButton>
            );
          })}
        </Group>
        {value.score != null && (
          <Text size="xs" c="dimmed" mt={spacing.xs}>
            {t(`review.q1.scale.${value.score}.description`)}
          </Text>
        )}
      </div>

      {hasModelValues && (
        <>
          <div>
            <Text fw={500} size="sm" mb={spacing.xs}>
              {t("review.q1.distinctness.title")}
            </Text>
            <Text size="xs" c="dimmed" mb={spacing.xs}>
              {t("review.q1.distinctness.help")}
            </Text>
            <Slider
              min={0}
              max={100}
              value={value.distinctness ?? 50}
              onChange={(v) => onChange({ ...value, distinctness: v })}
              aria-label={t("review.q1.distinctness.title")}
            />
          </div>
          <div>
            <Text fw={500} size="sm" mb={spacing.xs}>
              {t("review.q1.accuracy.title")}
            </Text>
            <Text size="xs" c="dimmed" mb={spacing.xs}>
              {t("review.q1.accuracy.help")}
            </Text>
            <Slider
              min={0}
              max={100}
              value={value.accuracy ?? 50}
              onChange={(v) => onChange({ ...value, accuracy: v })}
              aria-label={t("review.q1.accuracy.title")}
            />
          </div>
        </>
      )}
    </Stack>
  );
}
